import { useEffect, useRef } from "react";

/**
 * Task #7654 — shared localStorage draft persistence so long-text input
 * survives an accidental modal close / navigation / refresh.
 *
 * Envelope: `{ v, savedAt, data }` under `bac:draft:<key>`. Schema-versioned
 * (a version bump silently discards older drafts), TTL-expired on read, and
 * swept wholesale on sign-out via `clearAllDrafts()` (wired into
 * AuthProvider's clearAllAuthStorage through DRAFT_SIGNOUT_KEY_PREFIXES) so
 * one user's half-typed message can never surface for the next account on a
 * shared device.
 *
 * Usage contract:
 *  - The hook WRITES (debounced) whenever `values` changes while `enabled`.
 *  - RESTORE is caller-driven: call `readDraft()` at the moment the surface
 *    (re)opens and apply the fields you trust. This keeps seed/prefill
 *    precedence decisions at the callsite, where the domain rules live.
 *  - Call `clearDraft()` on successful submit — it also suppresses the
 *    trailing debounced write so the just-submitted text can't resurrect.
 */

export const DRAFT_KEY_PREFIX = "bac:draft:";

/**
 * Every prefix that must be purged on sign-out. Includes the review-flow
 * drafts (#7653) which predate this hook but carry the same cross-account
 * leak risk. Consumed by AuthProvider.clearAllAuthStorage.
 */
export const DRAFT_SIGNOUT_KEY_PREFIXES = [
  DRAFT_KEY_PREFIX,
  "bac:review-draft:",
] as const;

const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const WRITE_DEBOUNCE_MS = 400;

interface DraftEnvelope<T> {
  v: number;
  savedAt: number;
  data: T;
}

function storageKey(key: string): string {
  return `${DRAFT_KEY_PREFIX}${key}`;
}

export function readLocalDraft<T>(
  key: string,
  version: number,
  ttlMs: number = DEFAULT_TTL_MS,
): T | null {
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const env = JSON.parse(raw) as DraftEnvelope<T> | null;
    if (!env || typeof env !== "object" || env.v !== version) return null;
    if (typeof env.savedAt !== "number" || Date.now() - env.savedAt > ttlMs) {
      localStorage.removeItem(storageKey(key));
      return null;
    }
    if (env.data == null || typeof env.data !== "object") return null;
    return env.data;
  } catch {
    return null;
  }
}

export function writeLocalDraft<T>(key: string, version: number, data: T): void {
  try {
    localStorage.setItem(
      storageKey(key),
      JSON.stringify({ v: version, savedAt: Date.now(), data } satisfies DraftEnvelope<T>),
    );
  } catch {
    // Storage full / private browsing — drafts are best-effort.
  }
}

export function clearLocalDraft(key: string): void {
  try {
    localStorage.removeItem(storageKey(key));
  } catch {
    /* noop */
  }
}

/** Sign-out sweep — removes every draft under the known prefixes. */
export function clearAllDrafts(): void {
  try {
    const toDrop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (DRAFT_SIGNOUT_KEY_PREFIXES.some((p) => k.startsWith(p))) toDrop.push(k);
    }
    for (const k of toDrop) {
      try { localStorage.removeItem(k); } catch { /* noop */ }
    }
  } catch {
    /* noop */
  }
}

export interface UseLocalDraftResult<T> {
  /** Read the current stored draft (or null). Caller applies fields it trusts. */
  readDraft: () => T | null;
  /** Remove the draft and suppress the trailing debounced write. */
  clearDraft: () => void;
}

export function useLocalDraft<T extends object>(opts: {
  /** Draft key WITHOUT the `bac:draft:` prefix. Null disables the hook. */
  key: string | null;
  version: number;
  /** Current field values — persisted (debounced) whenever they change. */
  values: T;
  /** Gate writes (e.g. only while a modal is open). Default true. */
  enabled?: boolean;
  /** Skip the unmount/session-boundary flush during an ownership transition. Default true. */
  flushOnSessionEnd?: boolean;
  /** When true, the draft is removed instead of written (nothing worth keeping). */
  isEmpty?: (values: T) => boolean;
  ttlMs?: number;
}): UseLocalDraftResult<T> {
  const {
    key,
    version,
    values,
    enabled = true,
    flushOnSessionEnd = true,
    isEmpty,
    ttlMs = DEFAULT_TTL_MS,
  } = opts;

  // After clearDraft() (successful submit) we must not let the pending /
  // unmount-flush write resurrect the just-submitted draft. Suppression is
  // pinned to the `values` object that was live at clear time: it lifts as
  // soon as a NEW values object arrives (the user typed again — a fresh
  // draft session on always-open surfaces like the chat composer) or on the
  // next rising edge of `enabled` (modal reopened).
  const suppressRef = useRef(false);
  const suppressedValuesRef = useRef<T | null>(null);
  const prevEnabledRef = useRef(enabled);
  if (enabled && !prevEnabledRef.current) {
    suppressRef.current = false;
    suppressedValuesRef.current = null;
  }
  prevEnabledRef.current = enabled;

  const latestRef = useRef({ key, version, values, isEmpty });
  latestRef.current = { key, version, values, isEmpty };
  const flushOnSessionEndRef = useRef(flushOnSessionEnd);
  flushOnSessionEndRef.current = flushOnSessionEnd;

  // Values object live when the current (key, enabled) session opened. A
  // session that only ever renders its initial values (user opened and
  // closed without typing) must write NOTHING — otherwise a modal's
  // server-derived prefill gets persisted as a "draft" and can later
  // clobber fresher server values.
  const sessionInitialValuesRef = useRef<T | null>(null);

  // `sessionKey`/`sessionVersion` default to the live props but are pinned
  // by the session cleanup: when the key changes (e.g. the composer swaps
  // conversation threads) the flush must pair the OLD session's text with
  // the OLD key — latestRef already points at the new render by then.
  const writeNow = (sessionKey?: string | null, sessionVersion?: number) => {
    const cur = latestRef.current;
    if (cur.values === sessionInitialValuesRef.current) return;
    if (suppressRef.current) {
      if (cur.values === suppressedValuesRef.current) return;
      suppressRef.current = false;
      suppressedValuesRef.current = null;
    }
    const k = sessionKey !== undefined ? sessionKey : cur.key;
    const v = sessionVersion !== undefined ? sessionVersion : cur.version;
    if (!k) return;
    if (cur.isEmpty?.(cur.values)) {
      clearLocalDraft(k);
    } else {
      writeLocalDraft(k, v, cur.values);
    }
  };
  const writeNowRef = useRef(writeNow);
  writeNowRef.current = writeNow;

  // Session boundary: flush the final keystrokes exactly once, when the
  // surface closes / unmounts / re-keys — NOT on every values change (that
  // would defeat the debounce and write per keystroke).
  useEffect(() => {
    if (!key || !enabled) return;
    sessionInitialValuesRef.current = latestRef.current.values;
    return () => {
      if (flushOnSessionEndRef.current) writeNowRef.current(key, version);
      sessionInitialValuesRef.current = null;
    };
  }, [key, enabled, version]);

  // Debounced write while the session is live.
  useEffect(() => {
    if (!key || !enabled) return;
    if (values === sessionInitialValuesRef.current) return;
    const timer = setTimeout(() => writeNowRef.current(), WRITE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [key, enabled, version, values]);

  const readDraft = (): T | null =>
    latestRef.current.key ? readLocalDraft<T>(latestRef.current.key, latestRef.current.version, ttlMs) : null;

  const clearDraft = (): void => {
    suppressRef.current = true;
    suppressedValuesRef.current = latestRef.current.values;
    if (latestRef.current.key) clearLocalDraft(latestRef.current.key);
  };

  return { readDraft, clearDraft };
}
